<script setup lang="ts">
import { ref, watch } from 'vue'
import { ElUpload } from 'element-plus'
import { UploadFilled } from '@element-plus/icons-vue'
import type { UploadProps, UploadUserFile } from 'element-plus'
import { message } from '@/shared/lib'

/**
 * One picture picked for a form field — a logo, an avatar. A document is `FileUpload`,
 * which checks the extension instead; several files are `FilesUpload`.
 *
 * **Nothing is uploaded here**: the `File` goes into `v-model` and the form sends it with
 * the rest of the payload. A file that is not a JPG or PNG, or is over `maxSizeMb`, is
 * refused with a toast and the field is left empty — so a caller needs no validation of its
 * own. The drop zone names the formats and the limit itself unless `tip` says otherwise.
 */
const props = withDefaults(
  defineProps<{
    modelValue?: File | null
    accept?: string
    maxSizeMb?: number
    tip?: string
    block?: boolean
  }>(),
  {
    accept: '.jpg,.jpeg,.png',
    maxSizeMb: 2,
    tip: '',
    block: false
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', file: File | null): void
}>()

const fileList = ref<UploadUserFile[]>([])
const maxSizeBytes = props.maxSizeMb * 1024 * 1024

const defaultTip = `jpg, jpeg, png files with a size less than ${props.maxSizeMb}MB`

watch(
  () => props.modelValue,
  (file) => {
    if (!file) {
      fileList.value = []
    } else if (!fileList.value.length || fileList.value[0]?.raw !== file) {
      fileList.value = [
        {
          name: file.name
        }
      ]
    }
  },
  { immediate: true }
)

function validateFile(rawFile: File): boolean {
  const ok = ['image/jpeg', 'image/png'].includes(rawFile.type)
  if (!ok) {
    message.error('File must be JPG or PNG format')
    return false
  }
  if (rawFile.size > maxSizeBytes) {
    message.error(`File size must be less than ${props.maxSizeMb}MB`)
    return false
  }
  return true
}

const beforeUpload: UploadProps['beforeUpload'] = (rawFile) => {
  if (!validateFile(rawFile)) return false
  emit('update:modelValue', rawFile)
  return false
}

const handleRemove = () => {
  fileList.value = []
  emit('update:modelValue', null)
}

const handleChange: UploadProps['onChange'] = (uploadFile, uploadFiles) => {
  const raw = uploadFile.raw
  if (uploadFiles.length === 0) {
    emit('update:modelValue', null)
    return
  }
  if (raw) {
    if (!validateFile(raw)) {
      fileList.value = []
      emit('update:modelValue', null)
      return
    }
    emit('update:modelValue', raw)
  }
}
</script>

<template>
  <ElUpload
    v-model:file-list="fileList"
    class="image-upload"
    :class="{ 'image-upload--block': block }"
    drag
    :accept="accept"
    :auto-upload="false"
    :limit="1"
    :on-exceed="() => {}"
    :before-upload="beforeUpload"
    :on-remove="handleRemove"
    :on-change="handleChange"
    action="#"
  >
    <el-icon class="el-icon--upload"><UploadFilled /></el-icon>
    <div class="el-upload__text">
      Drop file here or <em>click to upload</em>
    </div>
    <template v-if="tip || defaultTip" #tip>
      <div class="el-upload__tip">
        {{ tip || defaultTip }}
      </div>
    </template>
  </ElUpload>
</template>

<style lang="scss" scoped>
.image-upload :deep(.el-upload-dragger) {
  width: 100%;
}

.image-upload--block {
  width: 100%;
}
</style>
